import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { providersService } from '../../shared/services/providers.service';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';

const DEFAULT_CURRENCY = 'EUR';
const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp,image/gif';
const MAX_BYTES = 10 * 1024 * 1024;

export function AddPortfolioImageDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const [imageUrl, setImageUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [previewError, setPreviewError] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const priceCents = useMemo(() => {
    if (!price.trim()) return undefined;
    const n = Number(price.replace(',', '.'));
    if (Number.isNaN(n) || n < 0) return undefined;
    return Math.round(n * 100);
  }, [price]);

  const reset = () => {
    setImageUrl('');
    setCaption('');
    setPrice('');
    setCurrency(DEFAULT_CURRENCY);
    setPreviewError(false);
    setUploadProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const upload = useMutation({
    mutationFn: (file: File) =>
      providersService.uploadPortfolioImage(file, (p) => setUploadProgress(p)),
    onMutate: () => setUploadProgress(0),
    onSuccess: (res) => {
      setImageUrl(res.url);
      setPreviewError(false);
      setUploadProgress(null);
    },
    onError: (err) => {
      setUploadProgress(null);
      notify.error(translate(err));
    },
  });

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (file.size > MAX_BYTES) {
      notify.error(t('provider.fileTooLarge'));
      return;
    }
    upload.mutate(file);
  };

  const submit = useMutation({
    mutationFn: () =>
      providersService.addPortfolioImage({
        imageUrl,
        caption: caption || undefined,
        priceCents,
        currency: priceCents != null ? currency : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['provider', 'me'] });
      notify.success(t('common.save'));
      reset();
      onClose();
    },
    onError: (err) => notify.error(translate(err)),
  });

  const isValidUrl = /^https?:\/\/.+/.test(imageUrl);
  const handleClose = () => {
    if (submit.isPending) return;
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" component="span">
          {t('provider.addPortfolioPiece')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('provider.addPortfolioHint')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {/* Live preview area */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '4 / 3',
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px dashed',
              borderColor: 'divider',
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.02)'
                  : 'rgba(0,0,0,0.02)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
            }}
          >
            {isValidUrl && !previewError ? (
              <Box
                key={imageUrl}
                component="img"
                src={imageUrl}
                alt={caption || 'preview'}
                onError={() => setPreviewError(true)}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            ) : (
              <Typography variant="body2" sx={{ px: 2, textAlign: 'center' }}>
                {t('provider.previewPlaceholder')}
              </Typography>
            )}

            {priceCents != null && isValidUrl && !previewError && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 12,
                  right: 12,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 2,
                  bgcolor: 'rgba(0,0,0,0.65)',
                  backdropFilter: 'blur(6px)',
                  color: 'common.white',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                }}
              >
                {new Intl.NumberFormat(undefined, {
                  style: 'currency',
                  currency: currency || DEFAULT_CURRENCY,
                }).format(priceCents / 100)}
              </Box>
            )}
          </Box>

          <Stack spacing={1}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
              disabled={upload.isPending || submit.isPending}
              fullWidth
            >
              {upload.isPending && uploadProgress != null
                ? t('provider.uploading', { percent: uploadProgress })
                : isValidUrl
                  ? t('provider.chooseAnotherPhoto')
                  : t('provider.uploadFromComputer')}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                hidden
                onChange={handleFileSelected}
              />
            </Button>
            {upload.isPending && uploadProgress != null && (
              <LinearProgress variant="determinate" value={uploadProgress} />
            )}
          </Stack>

          <TextField
            label={t('provider.caption')}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder={t('provider.captionPlaceholder')}
            fullWidth
            inputProps={{ maxLength: 280 }}
            helperText={`${caption.length}/280`}
          />

          <Stack direction="row" spacing={1.5}>
            <TextField
              label={t('provider.price')}
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder={t('provider.priceOptional')}
              inputProps={{ min: 0, step: '0.01' }}
              fullWidth
            />
            <TextField
              label={t('provider.currency')}
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))}
              sx={{ width: 110 }}
              disabled={!price}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submit.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!isValidUrl || submit.isPending || upload.isPending}
          onClick={() => submit.mutate()}
        >
          {submit.isPending ? t('common.loading') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
